import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { users } from '@/api/users';
import type { Role } from '@/lib/auth-store';
import { masquerade } from '@/api/auth';
import { PageHeader } from '@/components/PageHeader';
import { RoleBadge } from '@/components/RoleBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AgentPermissionsEditor } from '@/components/AgentPermissionsEditor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PhoneInput } from '@/components/PhoneInput';
import { Pencil, X, Check } from 'lucide-react';
import { formatRef } from '@/lib/formatRef';

/** Roles that can be assigned via the edit form (excludes privileged roles). */
const ASSIGNABLE_ROLES: Role[] = ['office_manager', 'standard_user', 'limited_user', 'agent'];

const ROLE_LABELS: Record<string, string> = {
  office_manager: 'Office Manager',
  standard_user: 'Standard User',
  limited_user: 'Limited User',
  agent: 'Agent',
};

/**
 * Client-side mirror of `backend/src/auth/services/masquerade.ts::canMasquerade`.
 * We hide the button when it would definitely be rejected by the server. The
 * server is still the source of truth — this is purely a UX guard.
 */
function canMasqueradeClient(
  actor: { id: string; role: string; is_super_admin: boolean },
  actorResellerId: string | null,
  target: { id: string; role: string; reseller_id: string | null },
): boolean {
  if (actor.id === target.id) return false;
  // UserRow.role === 'super_admin' is our only client-side signal; the
  // server's authoritative check also consults the `is_super_admin` flag
  // on the users table.
  if (target.role === 'super_admin') return false;
  if (target.role === 'reseller_owner') return false;
  if (actor.is_super_admin) return true;
  if (
    actor.role === 'reseller_owner' &&
    actorResellerId !== null &&
    actorResellerId === target.reseller_id
  ) {
    return true;
  }
  return false;
}

export function UserDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useCurrentUser();
  const meTenant = useAuth((s) => s.tenant);
  const [tempPassword, setTempPassword] = useState('');
  const [resendToken, setResendToken] = useState<string | null>(null);
  const [masqError, setMasqError] = useState<string | null>(null);

  // Email edit state (self-service email change for admins)
  const [editEmail, setEditEmail] = useState('');
  const [editEmailOpen, setEditEmailOpen] = useState(false);

  // Profile edit state
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editRole, setEditRole] = useState<Role>('standard_user');
  const [editError, setEditError] = useState<string | null>(null);

  const masqMutation = useMutation({
    mutationFn: (userId: string) => masquerade(userId),
    onSuccess: () => {
      // Hard reload so every query + route guard re-evaluates under the new
      // impersonated identity. Soft navigation would leave cached /auth/me
      // responses in place.
      window.location.assign('/dashboard');
    },
    onError: (err) => {
      setMasqError(err instanceof Error ? err.message : t('common.failedToSave'));
    },
  });

  // `id` existence guard is below; mutationFn closures here rely on UI-level
  // guarding. `id` may be a UUID or a bare ref number (/users/221 — rule 28);
  // the entered-customer context disambiguates bare refs for SA/RO, since
  // user numbers are per-scope.
  const effectiveCustomerId = useEffectiveCustomerId();
  const { data: u, isLoading, error } = useQuery({
    queryKey: ['user', id, effectiveCustomerId],
    queryFn: () => users.get(id!, effectiveCustomerId),
    enabled: Boolean(id),
  });

  const toggleActive = useMutation({
    mutationFn: () => users.update(id!, { active: !u!.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', id] }),
  });

  const resend = useMutation({
    mutationFn: () => users.resendInvite(id!),
    onSuccess: (v) => setResendToken(v.inviteToken),
  });

  const adminReset = useMutation({
    mutationFn: () => users.adminResetPassword(id!, tempPassword),
  });

  const sendResetEmail = useMutation({
    mutationFn: () => users.sendPasswordResetEmail(id!),
  });

  const changeEmail = useMutation({
    mutationFn: () => users.update(id!, { email: editEmail.toLowerCase().trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', id] });
      setEditEmailOpen(false);
      setEditEmail('');
    },
  });

  const del = useMutation({
    mutationFn: () => users.remove(id!),
    onSuccess: () => navigate('/users'),
  });

  const updateProfile = useMutation({
    mutationFn: () => users.update(id!, {
      display_name: editName.trim() || null,
      phone: editPhone.trim() || null,
      role: editRole,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', id] });
      setEditMode(false);
      setEditError(null);
    },
    onError: (err) => {
      setEditError(err instanceof Error ? err.message : t('common.failedToSave'));
    },
  });

  function startEdit() {
    if (!u) return;
    setEditName(u.display_name ?? '');
    setEditPhone(u.phone ?? '');
    setEditRole(ASSIGNABLE_ROLES.includes(u.role as Role) ? u.role : 'standard_user');
    setEditError(null);
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
    setEditError(null);
  }

  if (!id) return <div className="text-sm text-destructive">{t('common.invalidUrl')}</div>;
  if (isLoading) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  if (error || !u) return <div className="text-sm text-destructive">{error instanceof Error ? error.message : t('common.notFound')}</div>;

  const showMasquerade = canMasqueradeClient(
    { id: me.id, role: me.role, is_super_admin: me.is_super_admin },
    meTenant?.resellerId ?? null,
    { id: u.id, role: u.role, reseller_id: u.reseller_id },
  );

  return (
    <>
      <PageHeader
        eyebrow={t('nav.users')}
        title={u.display_name ?? u.email}
        description={[
          u.display_name ? u.email : undefined,
          u.ref_number ? formatRef(u.ref_number, 'USR') : undefined,
        ].filter(Boolean).join(' · ') || undefined}
        actions={
          <Button
            variant={u.active ? 'outline' : 'default'}
            disabled={toggleActive.isPending}
            onClick={() => toggleActive.mutate()}
          >
            {toggleActive.isPending
              ? (u.active ? t('users.deactivating') : t('users.activating'))
              : (u.active ? t('users.deactivate') : t('users.activate'))}
          </Button>
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 pb-3">
            <CardTitle className="text-base">{t('users.profileTitle')}</CardTitle>
            {!editMode ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                onClick={startEdit}
                aria-label="Edit profile"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  onClick={cancelEdit}
                  aria-label="Cancel edit"
                  disabled={updateProfile.isPending}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-green-600 hover:text-green-700"
                  onClick={() => updateProfile.mutate()}
                  aria-label="Save changes"
                  disabled={updateProfile.isPending}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {editMode ? (
              <div className="space-y-4 text-sm">
                {/* Email — always read-only in profile-edit mode */}
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">{t('common.email')}</Label>
                  <p className="text-sm break-all">{u.email}</p>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="edit-name" className="text-xs">{t('users.displayNameLabel')}</Label>
                  <Input
                    id="edit-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder={t('users.displayNameLabel')}
                    className="h-8 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="edit-phone" className="text-xs">{t('users.phoneLabel')}</Label>
                  <PhoneInput
                    id="edit-phone"
                    value={editPhone}
                    onChange={setEditPhone}
                    customerId={u.customer_id}
                    resellerId={u.reseller_id}
                    placeholder="555 000 0000"
                    inputClassName="h-8 text-sm"
                  />
                </div>

                {ASSIGNABLE_ROLES.includes(u.role as Role) && (
                  <div className="space-y-1">
                    <Label className="text-xs">{t('common.role')}</Label>
                    <Select
                      value={editRole}
                      onValueChange={(v) => setEditRole(v as Role)}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSIGNABLE_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {editError && (
                  <p role="alert" className="text-xs text-destructive">{editError}</p>
                )}

                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    disabled={updateProfile.isPending}
                    onClick={() => updateProfile.mutate()}
                  >
                    {updateProfile.isPending ? t('common.saving') : t('common.save')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={updateProfile.isPending}
                    onClick={cancelEdit}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
                <dt className="text-muted-foreground">{t('common.email')}</dt>
                <dd className="text-right sm:text-left break-all">
                  {editEmailOpen ? (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1">
                        <Input
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                          placeholder={u.email}
                          className="h-7 text-xs flex-1 min-w-0"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') changeEmail.mutate();
                            if (e.key === 'Escape') { setEditEmailOpen(false); setEditEmail(''); }
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-green-600 hover:text-green-700 shrink-0"
                          onClick={() => changeEmail.mutate()}
                          disabled={changeEmail.isPending || !editEmail.trim()}
                          aria-label={t('common.save')}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0"
                          onClick={() => { setEditEmailOpen(false); setEditEmail(''); }}
                          disabled={changeEmail.isPending}
                          aria-label={t('common.cancel')}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {changeEmail.isError && (
                        <p role="alert" className="text-xs text-destructive">
                          {changeEmail.error instanceof Error && (changeEmail.error as Error & { code?: string }).code === 'email_taken'
                            ? t('users.emailTaken', 'Email is already in use')
                            : (changeEmail.error instanceof Error ? changeEmail.error.message : t('common.failedToSave'))}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      {u.email}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => { setEditEmail(u.email); setEditEmailOpen(true); }}
                        aria-label={t('users.editEmail', 'Edit email')}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </span>
                  )}
                </dd>

                <dt className="text-muted-foreground">{t('users.displayNameLabel')}</dt>
                <dd className="text-right sm:text-left">{u.display_name ?? <span className="text-muted-foreground">—</span>}</dd>

                <dt className="text-muted-foreground">{t('users.phoneLabel')}</dt>
                <dd className="text-right sm:text-left">{u.phone ?? <span className="text-muted-foreground">—</span>}</dd>

                <dt className="text-muted-foreground">{t('common.role')}</dt>
                <dd className="text-right sm:text-left"><RoleBadge role={u.role} /></dd>

                <dt className="text-muted-foreground">{t('common.status')}</dt>
                <dd className="text-right sm:text-left">
                  <Badge variant={u.active ? 'default' : 'secondary'}>
                    {u.active ? t('common.active') : t('common.inactive')}
                  </Badge>
                </dd>

                <dt className="text-muted-foreground">{t('users.mfa')}</dt>
                <dd className="text-right sm:text-left">{u.mfa_enabled ? t('common.enabled') : t('common.disabled')}</dd>

                <dt className="text-muted-foreground">{t('users.lastLogin')}</dt>
                <dd className="text-right sm:text-left tabular-nums">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</dd>

                <dt className="text-muted-foreground">{t('common.createdAt')}</dt>
                <dd className="text-right sm:text-left tabular-nums">{new Date(u.created_at).toLocaleDateString()}</dd>
              </dl>
            )}
          </CardContent>
        </Card>

        {/* Actions card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('users.actionsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-sm">
            {/* Resend invite */}
            <div className="space-y-2">
              <p className="font-medium">{t('users.resendInvite')}</p>
              <Button
                variant="outline"
                size="sm"
                disabled={resend.isPending}
                onClick={() => resend.mutate()}
              >
                {resend.isPending ? t('users.sending') : t('users.resendInviteEmail')}
              </Button>
              {resend.isError && (
                <p role="alert" className="text-xs text-destructive">
                  {resend.error instanceof Error ? resend.error.message : t('users.failedToResend')}
                </p>
              )}
              {resendToken && (
                <p className="text-xs mt-1 text-muted-foreground break-all">
                  Link: <code className="bg-secondary px-1.5 py-0.5 rounded">{`${window.location.origin}/accept-invite?token=${resendToken}`}</code>
                </p>
              )}
            </div>

            {/* Send password reset email */}
            <div className="space-y-2">
              <p className="font-medium">Send password reset email</p>
              <Button
                variant="outline"
                size="sm"
                disabled={sendResetEmail.isPending}
                onClick={() => sendResetEmail.mutate()}
              >
                {sendResetEmail.isPending ? 'Sending…' : 'Send reset link'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Sends the user an email with a link to set a new password.
              </p>
              {sendResetEmail.isError && (
                <p role="alert" className="text-xs text-destructive">
                  {sendResetEmail.error instanceof Error ? sendResetEmail.error.message : t('common.failedToSave')}
                </p>
              )}
              {sendResetEmail.isSuccess && (
                <p className="text-xs text-green-600">Reset email sent!</p>
              )}
            </div>

            {/* Admin reset password */}
            <div className="space-y-2">
              <p className="font-medium">{t('users.adminResetPassword')}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="password"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  className="flex-1 min-w-0"
                  placeholder={t('users.tempPasswordPlaceholder')}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="sm:w-auto w-full"
                  disabled={tempPassword.length < 12 || adminReset.isPending}
                  onClick={() => adminReset.mutate()}
                >
                  {adminReset.isPending ? t('users.resetting') : t('users.reset')}
                </Button>
              </div>
              {adminReset.isError && (
                <p role="alert" className="text-xs text-destructive">
                  {adminReset.error instanceof Error ? adminReset.error.message : t('common.failedToSave')}
                </p>
              )}
              {adminReset.isSuccess && (
                <p className="text-xs text-primary">{t('users.passwordResetSuccess')}</p>
              )}
            </div>

            {/* Masquerade — only when actor can impersonate this user */}
            {showMasquerade && (
              <div className="space-y-2">
                <p className="font-medium">{t('users.masquerade')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('users.masqueradeDescription')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={masqMutation.isPending || !u.active}
                  onClick={() => {
                    setMasqError(null);
                    masqMutation.mutate(u.id);
                  }}
                >
                  {masqMutation.isPending ? t('users.starting') : t('users.masqueradeAs', { email: u.email })}
                </Button>
                {masqError && (
                  <p role="alert" className="text-xs text-destructive">{masqError}</p>
                )}
                {!u.active && (
                  <p className="text-xs text-muted-foreground">
                    {t('users.cannotMasqueradeInactive')}
                  </p>
                )}
              </div>
            )}

            {/* Delete */}
            <div className="space-y-2">
              <p className="font-medium">{t('users.deleteUser')}</p>
              <Button
                variant="destructive"
                size="sm"
                disabled={del.isPending}
                onClick={() => {
                  if (window.confirm(t('users.deleteConfirmMessage', { email: u.email }))) {
                    del.mutate();
                  }
                }}
              >
                {del.isPending ? t('common.deleting') : t('users.deleteUser')}
              </Button>
              {del.isError && (
                <p role="alert" className="text-xs text-destructive">
                  {del.error instanceof Error ? del.error.message : t('common.failedToDelete')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-user permissions matrix — ENFORCED as of 2026-07 (see
          backend/src/auth/middleware/requirePermission.ts). Rendered for the
          two matrix-governed roles: agents (reseller-level) and limited users
          (customer-level). Reads are allowed by default and can be revoked;
          edits are denied by default and can be granted per module.
          Editable by: super_admin (any), reseller_owner (their agents +
          limited users), office_manager (limited users in their customer). */}
      {(u.role === 'agent' || u.role === 'limited_user') && (
        <div className="mt-6">
          <AgentPermissionsEditor
            userId={u.id}
            canEdit={
              me.is_super_admin
              || me.role === 'super_admin'
              || me.role === 'reseller_owner'
              || (me.role === 'office_manager' && u.role === 'limited_user')
            }
          />
        </div>
      )}
    </>
  );
}
